import type { DocNode } from '@atlaskit/adf-schema/doc';

import { createAdfDerivationMemo } from './createAdfDerivationMemo.ts';

export type ParseAdfResult = { ok: true; doc: DocNode } | { ok: false; error: Error };

const parsedAdfByContent = createAdfDerivationMemo<ParseAdfResult>(2);

const parse = (adfString: string): ParseAdfResult => {
	try {
		return { ok: true, doc: JSON.parse(adfString) as DocNode };
	} catch (error) {
		return {
			ok: false,
			error: error instanceof Error ? error : new Error('Failed to parse ADF'),
		};
	}
};

export const parseAdfString = (contentId: string, adfString: string): ParseAdfResult => {
	// Unlike the reference-keyed WeakMap caches, this string-keyed LRU strongly retains
	// parsed documents. Bypass it during SSR so document objects are not retained or
	// reused across requests by the long-lived Node process.

	// This controls process-global cache retention, not rendered output.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return parse(adfString);
	}

	return parsedAdfByContent.derive(contentId, adfString, () => parse(adfString));
};

export const seedParsedAdf = (contentId: string, adfString: string, doc: DocNode): void => {
	// Keep the process-global memo client-only so SSR requests cannot retain or
	// reuse documents parsed while preparing another request.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return;
	}

	parsedAdfByContent.set(contentId, adfString, { ok: true, doc });
};
